import {Attributes, Card, DataStatus, openDialog, showNotification} from 'platform/components';
import {Heading, VStack} from 'platform/foundation';

import {useDispatch} from 'react-redux';

import {isNil} from 'ramda';

import {metadaRentalOrderApi, useRemoveRentalOrderServiceOrderMutation} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {workshopRoutes} from '@dms/routes';
import {Main, handleApiError} from '@dms/shared';

import {composePath, useRequiredParams} from 'shared/web';

import {PairWithServiceOrderForm} from '../../components/PairWithServiceOrderForm';
import {usePairedServiceOrder} from '../../hooks/usePairedServiceOrder';
import {useGetServiceOrderRows} from './hooks/useGetServiceOrderRows';

export function ServiceOrder() {
  const {id: rentalOrderId} = useRequiredParams();
  const dispatch = useDispatch();

  const [removeRentalOrderServiceOrder, {isLoading: isRemovingServiceOrder}] =
    useRemoveRentalOrderServiceOrderMutation();

  const handleRemoveServiceOrder = () => {
    removeRentalOrderServiceOrder({rentalOrderId})
      .unwrap()
      .then(() =>
        dispatch(
          metadaRentalOrderApi.util.invalidateTags([
            {type: 'serviceRentalOrder', id: rentalOrderId},
          ])
        )
      )
      .catch(handleApiError);
  };

  const {serviceOrder, serviceOrderJobs, isLoading, isError, isEmpty} =
    usePairedServiceOrder(rentalOrderId);

  const handlePairWithServiceOrder = () =>
    openDialog(<PairWithServiceOrderForm rentalOrderId={rentalOrderId} />, {
      title: i18n.t('page.rentalOrder.labels.pairWithServiceOrder'),
      size: 'large',
    });

  const title = isEmpty
    ? i18n.t('page.rentalOrder.labels.serviceOrder')
    : (serviceOrder?.name ?? i18n.t('page.rentalOrder.labels.serviceOrder'));

  const serviceOrderRows = useGetServiceOrderRows(rentalOrderId);
  const serviceOrderJobRows = serviceOrderJobs?.map((job) => ({
    label: i18n.t('general.labels.request'),
    value: job?.name,
  }));

  return (
    <Main>
      <Card
        title={title}
        actions={
          isEmpty
            ? []
            : [
                {
                  type: 'button',
                  variant: 'dangerLink',
                  title: i18n.t('general.actions.remove'),
                  leftIcon: 'content/link_off',
                  onClick: handleRemoveServiceOrder,
                  isLoading: isRemovingServiceOrder,
                },
                {
                  type: 'button',
                  variant: 'link',
                  title: i18n.t('general.actions.change'),
                  leftIcon: 'navigation/refresh',
                  onClick: handlePairWithServiceOrder,
                },
                {
                  type: 'button',
                  variant: 'link',
                  title: i18n.t('general.actions.view'),
                  leftIcon: 'action/open_in_new',
                  onClick: () => {
                    if (isNil(serviceOrder?.serviceCaseId)) {
                      showNotification.error();
                      return;
                    }

                    window.open(
                      composePath(workshopRoutes.serviceCaseDetail, {
                        params: {id: serviceOrder?.serviceCaseId},
                        queryParams: {
                          orderId: serviceOrder?.id,
                        },
                      })
                    );
                  },
                },
              ]
        }
      >
        <DataStatus
          isLoading={isLoading}
          isError={isError}
          spacing={8}
          isEmpty={isEmpty}
          emptyMessage={i18n.t('page.rentalOrder.labels.noServiceOrder')}
          action={{
            title: i18n.t('page.rentalOrder.labels.pairWithServiceOrder'),
            leftIcon: 'content/link',
            onClick: handlePairWithServiceOrder,
            variant: 'secondary',
          }}
        >
          <VStack spacing={4}>
            <Attributes rows={serviceOrderRows} size="third" />
            <Heading size={4}>{i18n.t('page.workshop.labels.serviceRequests')}</Heading>

            <DataStatus isEmpty={serviceOrderJobRows?.length === 0} spacing={10}>
              <Attributes rows={serviceOrderJobRows} size="third" />
            </DataStatus>
          </VStack>
        </DataStatus>
      </Card>
    </Main>
  );
}
